import { useEffect, useState } from "react";
import { Loader2, TrendingUp, TrendingDown, Minus, FileDown } from "lucide-react";
import { Button } from "../ui/button";
import { toast } from "sonner";
import { api, fmtXof } from "../../lib/api";

const PERIODS = [
  { key: "week", label: "Semaine" }, { key: "month", label: "Mois" },
  { key: "quarter", label: "Trimestre" }, { key: "semester", label: "Semestre" }, { key: "year", label: "Année" },
];

const DIR_COLOR = { hausse: "#16a34a", baisse: "#dc2626", stable: "#94a3b8" };

const Sparkline = ({ values, labels, color = "#0284C7", width = 90, height = 24 }) => {
  if (!values || values.length < 2) return <span className="text-[10px] text-slate-300">—</span>;
  const max = Math.max(...values), min = Math.min(...values);
  const span = max - min || 1;
  const step = width / (values.length - 1);
  const coord = (v, i) => [i * step, height - ((v - min) / span) * (height - 4) - 2];
  const pts = values.map((v, i) => coord(v, i).map((n) => n.toFixed(1)).join(",")).join(" ");
  return (
    <svg width={width} height={height} className="inline-block align-middle" data-testid="rank-sparkline">
      <polyline points={pts} fill="none" stroke={color} strokeWidth="1.5" strokeLinejoin="round" strokeLinecap="round" />
      {values.map((v, i) => {
        const [cx, cy] = coord(v, i);
        return (
          <circle key={i} cx={cx} cy={cy} r={i === values.length - 1 ? 2 : 3} fill={i === values.length - 1 ? color : "transparent"}
            className="cursor-pointer hover:fill-slate-700" data-testid={`rank-spark-pt-${i}`}>
            <title>{(labels?.[i] ? labels[i] + " : " : "")}{v}</title>
          </circle>
        );
      })}
    </svg>
  );
};

const DirBadge = ({ dir, significant }) => {
  const map = {
    hausse: { icon: TrendingUp, cls: "text-emerald-600", label: "Hausse" },
    baisse: { icon: TrendingDown, cls: "text-rose-600", label: "Baisse" },
    stable: { icon: Minus, cls: "text-slate-400", label: "Stable" },
  };
  const { icon: Icon, cls, label } = map[dir] || map.stable;
  return (
    <span className={`inline-flex items-center gap-1 text-xs font-semibold ${cls}`} title={significant ? "Tendance significative (p<0,05)" : "Tendance non significative"}>
      <Icon size={13} />{label}{significant && dir !== "stable" ? " *" : ""}
    </span>
  );
};

export const RankingCard = ({ endpoint, itemsKey, title, testid, nameLabel = "Produit", countLabel = "Ventes" }) => {
  const [period, setPeriod] = useState("month");
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get(`${endpoint}?period=${period}`).then(({ data }) => setData(data)).catch(() => {});
  }, [endpoint, period]);

  const exportRank = async (format) => {
    try {
      const res = await api.get(`${endpoint}/export?period=${period}&format=${format}`, { responseType: "blob" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(res.data); a.download = `${testid}-${period}.${format === "pdf" ? "pdf" : "xlsx"}`; a.click();
      toast.success("Export généré");
    } catch { toast.error("Export impossible"); }
  };

  const rows = data ? (data[itemsKey] || []) : [];

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-6" data-testid={testid}>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h3 className="font-display text-base font-semibold text-ciel">{title}</h3>
        <div className="flex flex-wrap items-center gap-2">
          <div className="flex rounded-full bg-slate-100 p-1">
            {PERIODS.map((p) => (
              <button key={p.key} data-testid={`${testid}-period-${p.key}`} onClick={() => setPeriod(p.key)}
                className={`rounded-full px-3 py-1 text-xs font-semibold transition-colors ${period === p.key ? "bg-white text-navy shadow-sm" : "text-slate-500 hover:text-navy"}`}>{p.label}</button>
            ))}
          </div>
          <Button data-testid={`${testid}-export-xlsx`} size="sm" variant="outline" onClick={() => exportRank("xlsx")} className="btn-emboss rounded-full"><FileDown size={13} className="mr-1" /> Excel</Button>
          <Button data-testid={`${testid}-export-pdf`} size="sm" onClick={() => exportRank("pdf")} className="btn-emboss rounded-full bg-navy text-white hover:bg-navy-light"><FileDown size={13} className="mr-1" /> PDF</Button>
        </div>
      </div>

      {!data ? (
        <div className="flex h-32 items-center justify-center"><Loader2 className="animate-spin text-ciel" size={20} /></div>
      ) : rows.length === 0 ? (
        <p className="mt-8 text-center text-sm text-slate-400">Aucune donnée sur cette période.</p>
      ) : (
        <div className="mt-4 overflow-x-auto">
          <table className="w-full text-sm" data-testid={`${testid}-table`}>
            <thead>
              <tr className="border-b border-slate-200 text-left text-xs uppercase tracking-wide text-slate-400">
                <th className="py-2 pr-3">#</th>
                <th className="py-2 pr-3">{nameLabel}</th>
                <th className="py-2 pr-3 text-right">{countLabel}</th>
                <th className="py-2 pr-3 text-right">CA</th>
                <th className="py-2 pr-3 text-center">Évolution</th>
                <th className="py-2 pr-3 text-right">Tendance</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((p, i) => (
                <tr key={p.product_id || p.client_id || i} className="border-b border-slate-100 last:border-0" data-testid={`${testid}-row-${i}`}>
                  <td className="py-2.5 pr-3 font-mono text-xs text-slate-400">{i + 1}</td>
                  <td className="py-2.5 pr-3 font-medium text-navy">{p.title || p.client_id || p.product_id}</td>
                  <td className="py-2.5 pr-3 text-right font-mono text-slate-700">{p.qty}</td>
                  <td className="py-2.5 pr-3 text-right font-mono text-slate-700">{fmtXof(p.ca)}</td>
                  <td className="py-2.5 pr-3 text-center"><Sparkline values={p.series} labels={data.labels} color={DIR_COLOR[p.direction] || "#0284C7"} /></td>
                  <td className="py-2.5 pr-3 text-right"><DirBadge dir={p.direction} significant={p.significant} /></td>
                </tr>
              ))}
            </tbody>
          </table>
          <p className="mt-3 text-[11px] italic text-slate-400">Tendance par régression linéaire ; « * » = significative (p &lt; 0,05). Survolez la mini-courbe pour voir les valeurs.</p>
        </div>
      )}
    </div>
  );
};
